/**
 * The guided-demo request — approved copy, 30 September 2026.
 *
 * One form, reachable from the report wait, the FAQ, Pricing and the
 * workspace. "Prefill details already supplied, keep editable, ask only for
 * missing information."
 *
 * Only the interest field is optional. Organization is reasonable here — this
 * is someone explicitly asking for a conversation — and is still absent from
 * the first brief screen, where §1 rules it out.
 */

import React, { useEffect, useRef, useState } from 'react';
import CountedTextarea from './CountedTextarea';
import { DEMO_FORM, SHARED } from './audienceCopy';

const isEmail = (v) => /^[^@\s,;]+@[^@.\s,;]+(\.[^@.\s,;]+)+$/.test(String(v || '').trim());

export default function DemoRequestForm({ prefill = {}, onSubmit, onBack, submitting, error }) {
  const [name, setName] = useState(prefill.name || '');
  const [email, setEmail] = useState(prefill.email || '');
  const [organization, setOrganization] = useState(prefill.organization || '');
  const [interest, setInterest] = useState('');
  const [sent, setSent] = useState(false);
  const [touched, setTouched] = useState(false);
  const nameRef = useRef(null);

  // The caret waits in the first field — see MockDemo.
  useEffect(() => { nameRef.current?.focus(); }, []);

  const valid = name.trim() && isEmail(email) && organization.trim();

  const submit = async () => {
    setTouched(true);
    if (!valid || submitting) return;
    await onSubmit({ name: name.trim(), email: email.trim(), organization: organization.trim(), interest: interest.trim() });
    setSent(true);
  };

  if (sent) {
    return (
      <>
        <p className="cmp-eyebrow">Guided demo</p>
        <h1 className="cmp-title">{DEMO_FORM.confirmation}</h1>
        {/* No response time promised — there is no owner and no service
            target behind one, and inventing one is a commitment nobody made. */}
        <div className="cmp-actions">
          <button type="button" className="cmp-btn cmp-btn--secondary" onClick={onBack}>
            ← {SHARED.back}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <p className="cmp-eyebrow">Guided demo</p>
      <h1 className="cmp-title">{DEMO_FORM.title}</h1>
      <p className="cmp-lede">{DEMO_FORM.lede}</p>

      {error && <div className="cmp-notice">{error}</div>}

      <div className="cmp-form">
        <div className="cmp-field">
          <label className="cmp-label" htmlFor="demo-name">{DEMO_FORM.name}</label>
          <input
            id="demo-name"
            ref={nameRef}
            className="cmp-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="cmp-field">
          <label className="cmp-label" htmlFor="demo-email">{DEMO_FORM.email}</label>
          <input
            id="demo-email"
            className="cmp-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="cmp-field">
          <label className="cmp-label" htmlFor="demo-org">{DEMO_FORM.organization}</label>
          <input
            id="demo-org"
            className="cmp-input"
            value={organization}
            onChange={(e) => setOrganization(e.target.value)}
          />
        </div>
        <div className="cmp-field">
          <label className="cmp-label" htmlFor="demo-interest">
            {DEMO_FORM.interest} <span className="cmp-optional">Optional</span>
          </label>
          <CountedTextarea
            id="demo-interest"
            wrapClassName="cmp-textarea-wrap"
            className="cmp-textarea"
            counterClassName="cmp-counter"
            style={{ minHeight: 96 }}
            value={interest}
            onChange={setInterest}
          />
        </div>

        {touched && !valid && (
          <div className="cmp-error">Please add your name, a work email and your organization.</div>
        )}

        <div className="cmp-actions">
          <button type="button" className="cmp-btn cmp-btn--secondary" onClick={onBack}>
            ← {SHARED.back}
          </button>
          <button
            type="button"
            className="cmp-btn cmp-btn--primary"
            onClick={submit}
            disabled={submitting}
          >
            {DEMO_FORM.submit}
          </button>
        </div>
      </div>
    </>
  );
}
